import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play, Pause, MapPin, Download, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Kinetic";
import EditorialMarquee from "@/components/Marquee";
import { usePlayer } from "@/context/PlayerContext";
import { api, startCheckout } from "@/lib/api";
import { formatShowDate } from "@/pages/Tour";

export default function Artist() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [buying, setBuying] = useState(null);
  const { current, playing, toggle } = usePlayer();

  useEffect(() => {
    setData(null);
    setMissing(false);
    api.get(`/artists/${id}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [id]);

  const buy = async (lookupKey, tid) => {
    setBuying(tid);
    try {
      await startCheckout(lookupKey);
    } catch {
      toast.error("Checkout failed — try again.");
      setBuying(null);
    }
  };

  if (missing) {
    return (
      <main data-testid="artist-not-found" className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <h1 className="font-anton text-5xl text-white">ARTIST NOT FOUND</h1>
        <Link to="/label" data-testid="artist-back-link" className="mt-8 font-mono text-xs tracking-[0.3em] text-primary">
          ← BACK TO THE LABEL
        </Link>
      </main>
    );
  }

  if (!data) {
    return <main className="min-h-screen bg-background" />;
  }

  const { artist, tracks, shows } = data;

  return (
    <main data-testid="artist-page" className="pb-40 pt-36">
      <div className="mx-auto max-w-7xl px-6">
        <Link to="/label" data-testid="artist-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.3em] text-white/50 transition-colors duration-300 hover:text-primary">
          <ArrowLeft size={14} /> THE LABEL
        </Link>
      </div>

      <div className="mx-auto mt-10 grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2">
        <div>
          <p className="mb-4 font-mono text-xs tracking-[0.4em] text-primary">{artist.role}</p>
          <h1 data-testid="artist-name" className="font-anton leading-[0.9]">
            <MaskedLine className="text-6xl text-white md:text-8xl" delay={0.1}>{artist.name}</MaskedLine>
          </h1>
          {artist.handle && (
            <p data-testid="artist-handle" className="mt-4 font-mono text-sm tracking-[0.3em] text-primary">{artist.handle}</p>
          )}
          {artist.bio && <p className="mt-8 max-w-lg leading-relaxed text-white/65">{artist.bio}</p>}
          <div data-testid="artist-stats" className="mt-10 grid grid-cols-2 gap-px border border-white/10 bg-white/10">
            <div className="bg-background p-6 text-center">
              <p className="font-anton text-3xl text-primary">{tracks.length}</p>
              <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-white/50">TRACKS</p>
            </div>
            <div className="bg-background p-6 text-center">
              <p className="font-anton text-3xl text-primary">{shows.length}</p>
              <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-white/50">SHOWS</p>
            </div>
          </div>

          {artist.photos?.length > 0 && (
            <div data-testid="artist-gallery" className="mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3">
              {artist.photos.map((ph, j) => (
                <img key={j} src={ph} alt={`${artist.name} gallery ${j + 1}`}
                  className="h-56 w-44 shrink-0 snap-center border border-white/10 object-cover" />
              ))}
            </div>
          )}
        </div>

        <Reveal>
          <div data-testid="artist-photo-frame" className="relative border border-primary/40 p-3">
            <div className="absolute -left-px -top-px h-8 w-8 border-l-2 border-t-2 border-primary" />
            <div className="absolute -bottom-px -right-px h-8 w-8 border-b-2 border-r-2 border-primary" />
            {artist.photo ? (
              <img src={artist.photo} alt={artist.name} data-testid="artist-photo" className="max-h-[560px] w-full object-cover" />
            ) : (
              <div data-testid="artist-photo" className="flex h-96 w-full items-center justify-center bg-black font-anton text-9xl text-primary/30">
                {artist.name.charAt(0)}
              </div>
            )}
          </div>
        </Reveal>
      </div>

      {tracks.length > 0 && (
        <section data-testid="artist-tracks" className="mx-auto mt-28 max-w-7xl px-6">
          <Reveal>
            <p className="mb-3 font-mono text-xs tracking-[0.4em] text-primary">THE CATALOG</p>
            <h2 className="font-anton text-5xl text-white md:text-6xl">TRACKS</h2>
          </Reveal>
          <div className="mt-10">
            {tracks.map((t, i) => {
              const isCurrent = current?.id === t.id;
              return (
                <Reveal key={t.id} delay={i * 0.05}>
                  <div data-testid={`artist-track-${t.id}`} className="group flex items-center gap-5 border-b border-white/10 py-5 transition-colors duration-300 hover:bg-white/[0.03]">
                    <span className="w-10 font-mono text-sm text-white/30">{String(i + 1).padStart(2, "0")}</span>
                    <button data-testid={`artist-track-play-${t.id}`} onClick={() => toggle(t)} aria-label={`Play ${t.title}`}
                      className="relative h-12 w-12 shrink-0 overflow-hidden border border-white/15">
                      <img src={t.cover} alt={t.title} className="h-full w-full object-cover" />
                      <span className={`absolute inset-0 flex items-center justify-center bg-black/50 ${isCurrent ? "text-primary" : "text-white opacity-0 group-hover:opacity-100"} transition-opacity duration-300`}>
                        {isCurrent && playing ? <Pause size={16} /> : <Play size={16} />}
                      </span>
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className={`truncate font-oswald font-bold tracking-wider ${isCurrent ? "text-primary" : "text-white"}`}>{t.title}</p>
                      <p className="truncate font-mono text-[10px] tracking-[0.2em] text-white/40">{t.album}</p>
                    </div>
                    <span className="hidden font-mono text-xs text-white/40 md:block">{t.duration}</span>
                    {t.free ? (
                      <a href={t.audio} download={`${t.title}.mp3`} data-testid={`artist-track-download-${t.id}`}
                        className="flex items-center gap-2 border border-white/25 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary">
                        <Download size={13} /> FREE DL
                      </a>
                    ) : (
                      <button data-testid={`artist-track-buy-${t.id}`} onClick={() => buy(t.lookup_key, t.id)} disabled={buying === t.id}
                        className="flex items-center gap-2 bg-primary px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50">
                        <ShoppingCart size={13} /> {buying === t.id ? "..." : `$${t.price.toFixed(2)}`}
                      </button>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {shows.length > 0 && (
        <section data-testid="artist-shows" className="mx-auto mt-28 max-w-7xl px-6">
          <Reveal>
            <p className="mb-3 font-mono text-xs tracking-[0.4em] text-primary">ON STAGE</p>
            <h2 className="font-anton text-5xl text-white md:text-6xl">UPCOMING SHOWS</h2>
          </Reveal>
          <div className="mt-10">
            {shows.map((s, i) => {
              const d = formatShowDate(s.date);
              return (
                <Reveal key={s.id} delay={i * 0.05}>
                  <div data-testid={`artist-show-${s.id}`} className="flex items-center gap-6 border-b border-white/10 py-5">
                    <span className="w-16 shrink-0 font-mono text-sm text-primary">{d.month} {d.day}</span>
                    <span className="flex-1 font-anton text-2xl text-white">{s.city}</span>
                    <span className="hidden items-center gap-2 font-mono text-xs tracking-[0.2em] text-white/40 md:flex">
                      <MapPin size={12} /> {s.venue}
                    </span>
                    <span className={`px-3 py-1 font-mono text-[10px] tracking-[0.25em] ${s.status === "SOLD OUT" ? "border border-white/25 text-white/50" : "bg-primary/15 text-primary"}`}>
                      {s.status}
                    </span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      <EditorialMarquee className="mt-28" outline speed={40} items={[artist.name, "SOUTHERN SMOKE", "THE LABEL"]} />
    </main>
  );
}
